'use client';

import { c as _c } from "react/compiler-runtime";
import type * as React from 'react';
import { CircleNotchIcon } from '@phosphor-icons/react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Slot as SlotPrimitive } from 'radix-ui';
import { useRipple } from './hooks/use-ripple';
import { cn } from './lib/cn';
const buttonVariants = cva('relative inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 overflow-hidden whitespace-nowrap font-inter font-medium outline-none transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:bg-role-disabled disabled:text-role-on-disabled aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none', {
  variants: {
    appearance: {
      filled: '',
      tonal: '',
      outlined: '',
      ghost: ''
    },
    color: {
      neutral: '',
      info: '',
      error: ''
    },
    rounded: {
      xs: 'rounded-sm',
      // 8px
      sm: 'rounded-md',
      // 16px
      md: 'rounded-lg',
      // 24px
      lg: 'rounded-xl',
      // 32px
      xl: 'rounded-4xl',
      // 64px
      full: 'rounded-full' // 64px
    }
  },
  compoundVariants: [
  // Neutral color variants - Using role-based colors from global.css
  {
    appearance: 'filled',
    color: 'neutral',
    class: 'bg-role-background-invert text-role-on-background-invert hover:bg-role-background-invert/70 active:bg-role-background-invert/90'
  }, {
    appearance: 'tonal',
    color: 'neutral',
    class: 'bg-role-surface-container text-role-on-surface-container hover:bg-black/12 active:bg-black/15'
  }, {
    appearance: 'outlined',
    color: 'neutral',
    class: 'active:black/12 border border-role-outline-subtle text-role-on-surface hover:bg-black/12'
  }, {
    appearance: 'ghost',
    color: 'neutral',
    class: 'text-role-on-surface hover:bg-black/12 active:bg-black/15'
  },
  // Info color variants
  {
    appearance: 'filled',
    color: 'info',
    class: 'bg-role-info text-role-on-info hover:bg-role-info/90 active:bg-role-info/95'
  }, {
    appearance: 'tonal',
    color: 'info',
    class: 'bg-role-info-container text-role-on-info-container hover:bg-role-info-container/80 active:bg-role-info-container/90'
  }, {
    appearance: 'outlined',
    color: 'info',
    class: 'border border-role-info-outline text-role-info hover:bg-role-info-container/50 active:bg-role-info-container/70'
  }, {
    appearance: 'ghost',
    color: 'info',
    class: 'text-role-info hover:bg-role-info-container/30 active:bg-role-info-container/50'
  },
  // error color variants
  {
    appearance: 'filled',
    color: 'error',
    class: 'bg-role-error text-role-on-error hover:bg-role-error/90 active:bg-role-error/95'
  }, {
    appearance: 'tonal',
    color: 'error',
    class: 'bg-role-error-container text-role-on-error-container hover:bg-role-error-container/80 active:bg-role-error-container/90'
  }, {
    appearance: 'outlined',
    color: 'error',
    class: 'border border-role-error-outline text-role-error hover:bg-role-error-container/50 active:bg-role-error-container/70'
  }, {
    appearance: 'ghost',
    color: 'error',
    class: 'text-role-error hover:bg-role-error-container/30 active:bg-role-error-container/50'
  }],
  defaultVariants: {
    appearance: 'filled',
    color: 'neutral',
    rounded: 'full'
  }
});

// Size lives outside cva because cva can't emit per-breakpoint classes.
// Each breakpoint map must hold the prefixed classes as literal strings so
// Tailwind's JIT can find them in source — runtime-built `${bp}:${cls}` would never be generated.
const BUTTON_SIZES = {
  xs: 'h-8 px-3 text-sm leading-[18px]',
  // XSmall: 32px height, 12px padding
  sm: 'h-9 px-4 text-sm leading-[18px]',
  // Small: 36px height, 16px padding
  md: 'h-14 px-6 text-lg leading-6',
  // Medium: 56px height, 24px padding
  lg: 'h-16 px-12 text-sm leading-[18px]',
  // Large: 64px height, 48px padding
  xl: 'h-34 px-12 text-sm leading-[18px]' // XLarge: 136px height, 48px padding
} as const;
const BUTTON_SIZES_SM = {
  xs: 'sm:h-8 sm:px-3 sm:text-sm sm:leading-[18px]',
  sm: 'sm:h-9 sm:px-4 sm:text-sm sm:leading-[18px]',
  md: 'sm:h-14 sm:px-6 sm:text-lg sm:leading-6',
  lg: 'sm:h-16 sm:px-12 sm:text-sm sm:leading-[18px]',
  xl: 'sm:h-34 sm:px-12 sm:text-sm sm:leading-[18px]'
} as const;
const BUTTON_SIZES_MD = {
  xs: 'md:h-8 md:px-3 md:text-sm md:leading-[18px]',
  sm: 'md:h-9 md:px-4 md:text-sm md:leading-[18px]',
  md: 'md:h-14 md:px-6 md:text-lg md:leading-6',
  lg: 'md:h-16 md:px-12 md:text-sm md:leading-[18px]',
  xl: 'md:h-34 md:px-12 md:text-sm md:leading-[18px]'
} as const;
const BUTTON_SIZES_LG = {
  xs: 'lg:h-8 lg:px-3 lg:text-sm lg:leading-[18px]',
  sm: 'lg:h-9 lg:px-4 lg:text-sm lg:leading-[18px]',
  md: 'lg:h-14 lg:px-6 lg:text-lg lg:leading-6',
  lg: 'lg:h-16 lg:px-12 lg:text-sm lg:leading-[18px]',
  xl: 'lg:h-34 lg:px-12 lg:text-sm lg:leading-[18px]'
} as const;
const BUTTON_SIZES_XL = {
  xs: 'xl:h-8 xl:px-3 xl:text-sm xl:leading-[18px]',
  sm: 'xl:h-9 xl:px-4 xl:text-sm xl:leading-[18px]',
  md: 'xl:h-14 xl:px-6 xl:text-lg xl:leading-6',
  lg: 'xl:h-16 xl:px-12 xl:text-sm xl:leading-[18px]',
  xl: 'xl:h-34 xl:px-12 xl:text-sm xl:leading-[18px]'
} as const;
const RESPONSIVE_BUTTON_SIZES = {
  sm: BUTTON_SIZES_SM,
  md: BUTTON_SIZES_MD,
  lg: BUTTON_SIZES_LG,
  xl: BUTTON_SIZES_XL
} as const;
type ButtonSize = keyof typeof BUTTON_SIZES;
type ResponsiveBreakpoint = keyof typeof RESPONSIVE_BUTTON_SIZES;
type ResponsiveButtonSize = ButtonSize | ({
  initial?: ButtonSize;
} & { [Breakpoint in ResponsiveBreakpoint]?: ButtonSize });
function resolveButtonSize(size: ResponsiveButtonSize) {
  if (typeof size === 'string') {
    return BUTTON_SIZES[size];
  }
  const {
    initial,
    ...breakpoints
  } = size;
  const base = initial ? BUTTON_SIZES[initial] : '';
  const responsive = Object.entries(breakpoints).filter(([, token]) => token).map(([breakpoint, token]) => RESPONSIVE_BUTTON_SIZES[breakpoint as ResponsiveBreakpoint][token as ButtonSize]);
  return cn(base, ...responsive);
}
export type ButtonProps = React.ComponentProps<'button'> & VariantProps<typeof buttonVariants> & {
  size?: ResponsiveButtonSize;
  isLoading?: boolean;
  asChild?: boolean;
};
function Button(t0) {
  const $ = _c(35);
  let appearance;
  let children;
  let className;
  let color;
  let onClick;
  let props;
  let rounded;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    ({
      className,
      appearance,
      color,
      size: t1,
      rounded,
      isLoading: t2,
      asChild: t3,
      children,
      onClick,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = appearance;
    $[2] = children;
    $[3] = className;
    $[4] = color;
    $[5] = onClick;
    $[6] = props;
    $[7] = rounded;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
  } else {
    appearance = $[1];
    children = $[2];
    className = $[3];
    color = $[4];
    onClick = $[5];
    props = $[6];
    rounded = $[7];
    t1 = $[8];
    t2 = $[9];
    t3 = $[10];
  }
  const size = t1 === undefined ? "sm" : t1;
  const isLoading = t2 === undefined ? false : t2;
  const asChild = t3 === undefined ? false : t3;
  const Comp = asChild ? SlotPrimitive.Slot : "button";
  const {
    ripples,
    createRipple
  } = useRipple();
  let t4;
  if ($[11] !== createRipple || $[12] !== onClick) {
    t4 = event => {
      createRipple(event);
      onClick?.(event);
    };
    $[11] = createRipple;
    $[12] = onClick;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const handleClick = t4;
  let t5;
  if ($[14] !== appearance || $[15] !== className || $[16] !== color || $[17] !== rounded || $[18] !== size) {
    t5 = cn(buttonVariants({
      appearance,
      color,
      rounded
    }), resolveButtonSize(size), className);
    $[14] = appearance;
    $[15] = className;
    $[16] = color;
    $[17] = rounded;
    $[18] = size;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  const t6 = asChild ? onClick : handleClick;
  let t7;
  if ($[20] !== isLoading) {
    t7 = isLoading && <CircleNotchIcon className="animate-spin" size={20} />;
    $[20] = isLoading;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const t8 = !isLoading && children;
  let t9;
  if ($[22] !== t8) {
    t9 = <SlotPrimitive.Slottable>{t8}</SlotPrimitive.Slottable>;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== asChild || $[25] !== ripples) {
    t10 = !asChild && <span className="pointer-events-none absolute inset-0">{ripples.map(_temp)}</span>;
    $[24] = asChild;
    $[25] = ripples;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== Comp || $[28] !== props || $[29] !== t10 || $[30] !== t5 || $[31] !== t6 || $[32] !== t7 || $[33] !== t9) {
    t11 = <Comp className={t5} data-slot="button" onClick={t6} {...props}>{t7}{t9}{t10}</Comp>;
    $[27] = Comp;
    $[28] = props;
    $[29] = t10;
    $[30] = t5;
    $[31] = t6;
    $[32] = t7;
    $[33] = t9;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  return t11;
}
function _temp(ripple) {
  return <span className="absolute animate-ripple rounded-full bg-current opacity-30" key={ripple.id} style={{
    left: ripple.x,
    top: ripple.y,
    width: ripple.size,
    height: ripple.size
  }} />;
}
export type { ButtonSize, ResponsiveButtonSize };
export { Button, buttonVariants, resolveButtonSize };